import { useEffect, useState, useRef } from "react";
import { LoadPanel, SelectBox } from "devextreme-react";
import notify from "devextreme/ui/notify";
import { useTranslation } from "react-i18next";
import { getCurrentUser } from "../../../api/auth";
import {
    Ticket, TicketStatus, TicketPriority, TICKET_STATUS_DEFS,
    TicketMessage, TicketHistory, SupportUser, CannedResponse, SatisfactionSurvey,
    getTicket, getTicketMessages, getTicketHistory, getSupportUsers,
    getCannedResponses, updateTicket,
    updateTicketStatus, assignTicket, createTicketHistory, sendMessage,
    getSurveyByTicket,
} from "../../../api/support";
import {
    STATUS_TEXT_KEYS, STATUS_BADGE, STATUS_DOT,
    PRIORITY_TEXT_KEYS, PRIORITY_BADGE, PRIORITY_DOT, PRIORITY_ICON,
    PRODUCT_TEXT, PRODUCT_COLOR, getSlaInfo, formatSlaRemaining,
} from "./ticket-constants";
import { GeneralTab } from "./GeneralTab";
import { MessagesTab } from "./MessagesTab";
import { HistoryTab } from "./HistoryTab";
import { VendorInfoPane } from "../vendors/VendorInfoPane";
import { DEMO_VENDOR_REF } from "../vendors/vendor-mock";
import "./TicketPage.css";

export function TicketDetailPane({ ticketId }: { ticketId: string }) {
    const { t } = useTranslation();
    const user = getCurrentUser();
    const [activeTab, setActiveTab] = useState(1); // varsayilan: Mesajlar (agent ilk burayi gormek ister)

    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [messages, setMessages] = useState<TicketMessage[]>([]);
    const [ticketHistory, setTicketHistory] = useState<TicketHistory[]>([]);
    const [supportUsers, setSupportUsers] = useState<SupportUser[]>([]);
    const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);

    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [priorityMenuOpen, setPriorityMenuOpen] = useState(false);
    const statusBtnRef = useRef<HTMLButtonElement>(null);
    const priorityBtnRef = useRef<HTMLButtonElement>(null);
    const [statusMenuPos, setStatusMenuPos] = useState({ top: 0, left: 0 });
    const [priorityMenuPos, setPriorityMenuPos] = useState({ top: 0, left: 0 });
    const [selectedAssignee, setSelectedAssignee] = useState("");
    const [assignNote, setAssignNote] = useState("");
    const [survey, setSurvey] = useState<SatisfactionSurvey | null>(null);
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        setEditMode(false); // ticket degisince edit modundan cik
        loadTicketDetail(ticketId);
    }, [ticketId]);

    const loadTicketDetail = async (id: string) => {
        setDetailLoading(true);
        try {
            const [ticketData, msgsData, histData, usersData, cannedData, surveyData] = await Promise.all([
                getTicket(id), getTicketMessages(id), getTicketHistory(id), getSupportUsers(), getCannedResponses(), getSurveyByTicket(id),
            ]);
            setTicket(ticketData); setMessages(msgsData); setTicketHistory(histData);
            setSupportUsers(usersData); setCannedResponses(cannedData); setSurvey(surveyData);
        } catch {
            notify(t("ticketDetailLoadError"), "error", 2000);
        } finally {
            setDetailLoading(false);
        }
    };

    // Bu durumlara gecmeden once destek grubu atanmis olmali.
    const STATUS_REQUIRES_GROUP: TicketStatus[] = [3, 9, 10]; // ASSIGNED, RESOLVED, CLOSED

    const handleUpdateStatus = async (newStatus: TicketStatus) => {
        if (!ticket) return;
        // Destek grubu zorunlulugu: grup yoksa bu gecislere izin verme.
        if (STATUS_REQUIRES_GROUP.includes(newStatus) && !ticket.support_group) {
            setStatusMenuOpen(false);
            notify(t("supportGroupRequired"), "warning", 3000);
            return;
        }
        const oldStatus = ticket.status;
        setStatusMenuOpen(false); setDetailLoading(true);
        try {
            await updateTicketStatus(ticket.ref, newStatus as any);
            await createTicketHistory({ ticket_ref: ticket.ref, action: "status_changed", old_value: t(STATUS_TEXT_KEYS[oldStatus]), new_value: t(STATUS_TEXT_KEYS[newStatus]), performed_by: user?.id, performed_by_name: user?.name });
            setTicket({ ...ticket, status: newStatus });
            setTicketHistory(await getTicketHistory(ticket.ref));
            notify(t("statusUpdated"), "success", 2000);
        } catch { notify(t("statusUpdateError"), "error", 2000); }
        finally { setDetailLoading(false); }
    };

    const handleUpdatePriority = async (newPriority: TicketPriority) => {
        if (!ticket) return;
        const oldPriority = ticket.priority;
        setPriorityMenuOpen(false); setDetailLoading(true);
        try {
            await updateTicket(ticket.ref, { priority: newPriority });
            await createTicketHistory({ ticket_ref: ticket.ref, action: "priority_changed", old_value: t(PRIORITY_TEXT_KEYS[oldPriority]), new_value: t(PRIORITY_TEXT_KEYS[newPriority]), performed_by: user?.id, performed_by_name: user?.name });
            setTicket({ ...ticket, priority: newPriority });
            setTicketHistory(await getTicketHistory(ticket.ref));
            notify(t("priorityUpdated"), "success", 2000);
        } catch { notify(t("priorityUpdateError"), "error", 2000); }
        finally { setDetailLoading(false); }
    };

    // Genel sekmesi edit modu kaydet (6502 / updateTicket).
    const handleSaveGeneral = async (patch: Partial<Ticket>) => {
        if (!ticket) return;
        setDetailLoading(true);
        try {
            await updateTicket(ticket.ref, patch);
            setTicket({ ...ticket, ...patch });
            setEditMode(false);
            notify(t("ticketUpdated"), "success", 2000);
        } catch {
            notify(t("ticketUpdateError"), "error", 2000);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleAssignTicket = async () => {
        if (!ticket || !selectedAssignee) return;
        const oldName = ticket.assigned_to_name || t("unassigned");
        setDetailLoading(true);
        try {
            await assignTicket(ticket.ref, selectedAssignee, user?.id || "");
            const assignedUser = supportUsers.find(u => u.ref === selectedAssignee);
            const newName = assignedUser?.full_name || t("unknown");
            await createTicketHistory({ ticket_ref: ticket.ref, action: "assigned", old_value: oldName, new_value: newName, performed_by: user?.id, performed_by_name: user?.name });
            if (assignNote.trim()) {
                await sendMessage(ticket.ref, `<p><strong>${t("assignmentNote")}</strong></p>${assignNote}`, user?.id || "unknown", user?.name || t("supportTeam"), true);
            }
            setTicket({ ...ticket, assigned_to: selectedAssignee, assigned_to_name: assignedUser?.full_name });
            setSelectedAssignee(""); setAssignNote("");
            setTicketHistory(await getTicketHistory(ticket.ref));
            notify(t("ticketAssigned", { name: newName }), "success", 2000);
        } catch { notify(t("ticketAssignError"), "error", 2000); }
        finally { setDetailLoading(false); }
    };

    if (!ticket) {
        return (
            <div className="h-full flex items-center justify-center" style={{ background: 'var(--base-bg)', color: 'var(--subtitle-text-color)' }}>
                <LoadPanel visible={detailLoading} />
                {!detailLoading && t("loadingText")}
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col" style={{ background: 'var(--base-bg)' }}>
            <LoadPanel visible={detailLoading} />

            {/* Detail Header */}
            <div className="ticket-detail-header" style={{ background: 'var(--base-bg-darken-4)', borderBottom: '1px solid var(--border-color)' }}>
                {/* Baslik satiri: #kod - baslik */}
                <div className="flex items-center gap-2 px-3 pt-2">
                    <span className="font-mono text-xs px-1.5 py-0.5 rounded shrink-0" style={{ color: 'var(--subtitle-text-color)', background: 'var(--base-bg)', border: '1px solid var(--border-color)' }}>
                        #{ticket.ticket_code}
                    </span>
                    <span className="text-sm font-semibold truncate" style={{ color: 'var(--base-text-color)' }} title={ticket.title}>
                        {ticket.title}
                    </span>
                </div>
                {/* Rozet satiri */}
                <div className="flex flex-wrap items-center gap-1.5 px-3 py-2">
                    <span className="text-[10px] font-semibold shrink-0" style={{ color: PRODUCT_COLOR[ticket.product_code] ?? '#6B7280' }}>
                        {PRODUCT_TEXT[ticket.product_code] ?? `Urun ${ticket.product_code}`}
                    </span>

                    {/* Status dropdown */}
                    <div>
                        <button ref={statusBtnRef} onClick={() => {
                            if (!statusMenuOpen && statusBtnRef.current) {
                                const r = statusBtnRef.current.getBoundingClientRect();
                                setStatusMenuPos({ top: r.bottom + 6, left: r.left });
                            }
                            setStatusMenuOpen(v => !v); setPriorityMenuOpen(false);
                        }}
                            className="flex items-center gap-1 text-[11px] font-semibold transition-all"
                            style={{ color: STATUS_BADGE[ticket.status] }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_BADGE[ticket.status] }} />
                            {t(STATUS_TEXT_KEYS[ticket.status])}
                            <svg className={`w-2.5 h-2.5 opacity-40 transition-transform ${statusMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {statusMenuOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setStatusMenuOpen(false)} />
                                <div className="fixed rounded-xl py-1.5 z-50 min-w-[180px] ticket-dropdown"
                                    style={{ top: statusMenuPos.top, left: statusMenuPos.left }}>
                                    {TICKET_STATUS_DEFS.filter(d => d.isActive).map(d => d.nr).map(s => (
                                        <button key={s} onClick={() => handleUpdateStatus(s)}
                                            className={`w-full text-left px-3.5 py-2 text-xs flex items-center gap-2.5 transition-colors ticket-dropdown-item ${ticket.status === s ? "active" : ""}`}>
                                            <span className={`w-2 h-2 rounded-full ${STATUS_DOT[s]}`} />
                                            {t(STATUS_TEXT_KEYS[s])}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Priority dropdown */}
                    <div>
                        <button ref={priorityBtnRef} onClick={() => {
                            if (!priorityMenuOpen && priorityBtnRef.current) {
                                const r = priorityBtnRef.current.getBoundingClientRect();
                                setPriorityMenuPos({ top: r.bottom + 6, left: r.left });
                            }
                            setPriorityMenuOpen(v => !v); setStatusMenuOpen(false);
                        }}
                            className="flex items-center gap-1 text-[11px] font-semibold transition-all"
                            style={{ color: PRIORITY_BADGE[ticket.priority] }}>
                            <span>{PRIORITY_ICON[ticket.priority]}</span>
                            {t(PRIORITY_TEXT_KEYS[ticket.priority])}
                            <svg className={`w-2.5 h-2.5 opacity-40 transition-transform ${priorityMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {priorityMenuOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setPriorityMenuOpen(false)} />
                                <div className="fixed rounded-xl py-1.5 z-50 min-w-[150px] ticket-dropdown"
                                    style={{ top: priorityMenuPos.top, left: priorityMenuPos.left }}>
                                    {([1, 2, 3] as TicketPriority[]).map(p => (
                                        <button key={p} onClick={() => handleUpdatePriority(p)}
                                            className={`w-full text-left px-3.5 py-2 text-xs flex items-center gap-2.5 transition-colors ticket-dropdown-item ${ticket.priority === p ? "active" : ""}`}>
                                            <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[p]}`} />
                                            {t(PRIORITY_TEXT_KEYS[p])}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* SLA badge */}
                    {(() => {
                        const sla = getSlaInfo(ticket);
                        if (sla.state === "none") return null;
                        const remaining = formatSlaRemaining(sla.remainingMs as number, t);
                        const label = sla.state === "overdue"
                            ? t("slaOverdueBy", { time: remaining })
                            : t("slaRemaining", { time: remaining });
                        return (
                            <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: sla.color }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: sla.color }} />
                                SLA: {label}
                            </span>
                        );
                    })()}

                    <div className="w-px h-4 ticket-divider" />

                    {/* Assign */}
                    <button onClick={() => setActiveTab(3)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium transition-colors ticket-assign-btn">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {ticket.assigned_to_name || t("assign")}
                    </button>

                    {/* Takim (destek grubu) */}
                    {ticket.support_group_name && (
                        <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--subtitle-text-color)' }}>
                            <i className="dx-icon-group" />
                            {ticket.support_group_name}
                        </span>
                    )}
                </div>
            </div>

            {/* Custom Tabs */}
            <div className="detail-tabs-bar">
                {[
                    { label: t("generalTab"), idx: 0, icon: "dx-icon-info" },
                    { label: t("messagesTab", { count: messages.length }), idx: 1, icon: "dx-icon-email" },
                    { label: t("internalNotesTab"), idx: 5, icon: "dx-icon-comment" },
                    { label: t("historyTab", { count: ticketHistory.length }), idx: 2, icon: "dx-icon-clock" },
                    { label: t("assignTab"), idx: 3, icon: "dx-icon-user" },
                    { label: t("vendorTab"), idx: 4, icon: "dx-icon-group" },
                ].map(tab => (
                    <button key={tab.idx}
                        className={`detail-tab-btn ${activeTab === tab.idx ? "active" : ""}`}
                        onClick={() => setActiveTab(tab.idx)}>
                        <i className={`${tab.icon} detail-tab-icon`} />
                        {tab.label}
                    </button>
                ))}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {activeTab === 0 && (
                    <GeneralTab
                        ticket={ticket}
                        survey={survey}
                        editMode={editMode}
                        saving={detailLoading}
                        onEdit={() => setEditMode(true)}
                        onSave={handleSaveGeneral}
                        onCancel={() => setEditMode(false)}
                    />
                )}
                {activeTab === 1 && (
                    <MessagesTab
                        ticket={ticket}
                        messages={messages}
                        cannedResponses={cannedResponses}
                        detailLoading={detailLoading}
                        onMessagesUpdate={setMessages}
                        onLoadingChange={setDetailLoading}
                    />
                )}
                {activeTab === 5 && (
                    <MessagesTab
                        ticket={ticket}
                        messages={messages}
                        cannedResponses={cannedResponses}
                        detailLoading={detailLoading}
                        onMessagesUpdate={setMessages}
                        onLoadingChange={setDetailLoading}
                        internalOnly
                    />
                )}
                {activeTab === 2 && <HistoryTab ticketHistory={ticketHistory} />}
                {/* Gecici: backend baglanana kadar sabit DEMKA gosterilir (ticket.vendor_ref yerine) */}
                {activeTab === 4 && <VendorInfoPane vendorRef={String(DEMO_VENDOR_REF)} />}
                {activeTab === 3 && (
                    <div className="h-full overflow-auto p-4">
                        <div className="max-w-lg space-y-5">
                            {/* Current assignee */}
                            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--base-bg-darken-4)', border: '1px solid var(--border-color)' }}>
                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                                    style={{ background: ticket.assigned_to_name ? 'var(--accent-color)' : 'var(--border-color)' }}>
                                    {ticket.assigned_to_name ? ticket.assigned_to_name.charAt(0).toUpperCase() : '?'}
                                </div>
                                <div>
                                    <p className="text-xs" style={{ color: 'var(--subtitle-text-color)' }}>{t("currentAssignee")}</p>
                                    <p className="text-sm font-semibold" style={{ color: 'var(--base-text-color)' }}>
                                        {ticket.assigned_to_name || t("unassigned")}
                                    </p>
                                </div>
                            </div>

                            {/* New assignee select */}
                            <div>
                                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--subtitle-text-color)' }}>{t("supportUser")}</label>
                                <SelectBox
                                    dataSource={supportUsers}
                                    value={selectedAssignee}
                                    valueExpr="ref"
                                    displayExpr="full_name"
                                    onValueChanged={e => setSelectedAssignee(e.value)}
                                    placeholder={t("selectUser")}
                                    searchEnabled
                                    width="100%"
                                />
                            </div>

                            {/* Note */}
                            <div>
                                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--subtitle-text-color)' }}>
                                    {t("noteOptional")} <span style={{ fontWeight: 400 }}>({t("optional")})</span>
                                </label>
                                <textarea
                                    className="w-full rounded-xl p-3 text-sm resize-none transition-shadow ticket-popup-textarea"
                                    rows={3}
                                    value={assignNote}
                                    onChange={e => setAssignNote(e.target.value)}
                                    placeholder={t("assignNotePlaceholder")}
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2.5">
                                <button
                                    onClick={handleAssignTicket}
                                    disabled={!selectedAssignee || detailLoading}
                                    className="px-5 py-2.5 text-sm rounded-xl bg-gradient-to-r from-slate-700 to-slate-800 text-white hover:from-slate-800 hover:to-slate-900 font-semibold transition-all disabled:opacity-40 shadow-sm"
                                >
                                    {t("assign")}
                                </button>
                                <button
                                    onClick={() => { setSelectedAssignee(""); setAssignNote(""); }}
                                    className="px-4 py-2.5 text-sm rounded-xl font-medium transition-colors ticket-popup-cancel"
                                >
                                    {t("clear")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
}
